import React from "react";
import { Check, Circle, X as XIcon } from "lucide-react";
import {
  evaluatePassword,
  MIN_PASSWORD_LEN,
  REQUIRED_CLASSES,
} from "../lib/passwordPolicy";

/**
 * Live password-policy guidance — mirrors backend `_validate_password`
 * (tenant) and `_validate_operator_password` (operator). Renders an
 * accessible checklist that updates on every keystroke.
 *
 * Props:
 *   value                  — current password string
 *   minLength              — min characters (default 12, operator 20)
 *   requiredClasses        — minimum class count (default 3, operator 4)
 *   forbiddenSubstrings    — list of literal strings the password may
 *                            NOT contain (case-insensitive). Operator
 *                            policy passes ["shelflife","shelf-life",
 *                            "console-operator","blueharborlabs"] here.
 *   email                  — used to derive local-part + domain
 *                            substrings to forbid (operator only).
 *   isOperator             — when true, displays operator-policy label
 *                            and shows the forbidden-substring rule.
 *
 * Each row has `data-testid="pw-rule-{name}"` + `data-state="met"|"unmet"`
 * so tests can assert state without coupling to copy. Guidance only —
 * submit button is NOT disabled from here; backend remains authoritative.
 */
function Row({ name, ok, severity = "ok", children }) {
  const Icon = ok
    ? Check
    : severity === "violation"
      ? XIcon
      : Circle;
  const colorClass = ok
    ? "text-shelf-fresh"
    : severity === "violation"
      ? "text-shelf-expired"
      : "text-ink/55";
  return (
    <li
      className={`flex items-start gap-2 transition-colors ${colorClass}`}
      data-testid={`pw-rule-${name}`}
      data-state={ok ? "met" : severity === "violation" ? "violation" : "unmet"}
    >
      <span className="shrink-0 mt-0.5" aria-hidden="true">
        <Icon size={14} strokeWidth={2.8} />
      </span>
      <span className="font-mono text-[11px] leading-snug">{children}</span>
    </li>
  );
}

export default function PasswordChecklist({
  value = "",
  minLength,
  requiredClasses,
  forbiddenSubstrings = [],
  email = "",
  isOperator = false,
}) {
  const effMin = minLength ?? MIN_PASSWORD_LEN;
  const effClasses = requiredClasses ?? REQUIRED_CLASSES;
  const ev = evaluatePassword(value, {
    minLength: effMin,
    requiredClasses: effClasses,
    forbiddenSubstrings,
    email,
  });

  return (
    <div
      className="mt-2 border border-border rounded-sm bg-secondary/40 px-3 py-2"
      data-testid="password-checklist"
      data-overall-state={ev.meetsAllRules ? "met" : "unmet"}
      data-class-count={ev.classCount}
      data-policy={isOperator ? "operator" : "tenant"}
      data-min-length={effMin}
      data-required-classes={effClasses}
      aria-live="polite"
    >
      <div className="font-mono text-[10px] uppercase tracking-wider text-ink/60 mb-1.5">
        {isOperator ? "Operator password must include" : "Password must include"}
      </div>
      <ul className="space-y-1">
        <Row name="length" ok={ev.lengthOk}>
          At least {effMin} characters
          <span className="ml-1 text-ink/40">
            ({ev.length}/{effMin})
          </span>
        </Row>
        <Row name="classes" ok={ev.classesOk}>
          {effClasses} of: uppercase, lowercase, number, special
          <span className="ml-1 text-ink/40">
            ({ev.classCount}/4 detected)
          </span>
        </Row>
        {isOperator && (
          <Row
            name="forbidden"
            ok={ev.forbiddenOk}
            severity="violation"
          >
            {ev.forbiddenOk
              ? "Does not contain brand or email-derived words"
              : `Cannot contain: ${ev.forbiddenHits.join(", ")}`}
          </Row>
        )}
      </ul>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1 mt-2 pt-2 border-t border-border/60">
        <Row name="upper" ok={ev.hasUpper}>
          Uppercase A–Z
        </Row>
        <Row name="lower" ok={ev.hasLower}>
          Lowercase a–z
        </Row>
        <Row name="digit" ok={ev.hasDigit}>
          Number 0–9
        </Row>
        <Row name="special" ok={ev.hasSpecial}>
          Special e.g. !@#-_
        </Row>
      </ul>
    </div>
  );
}
